import { cn } from '@packages/ui/styling/cn';
import type { ReactNode } from 'react';

export interface PageLayoutProps {
  children: ReactNode;
  className?: string;
  /**
   * Controls how the page handles scrolling.
   * - 'document' (default): The page grows with content and the window scrolls. Use for long forms or dashboards.
   * - 'viewport': The page is fixed to the viewport height. Content must handle its own scrolling. Use for "app-like" views with fixed headers/footers.
   */
  scrolling?: 'document' | 'viewport';
}

/**
 * Page-level layout with the app's gradient background.
 * Centers its children horizontally and provides consistent
 * page-level spacing. Use as the outermost wrapper for any full-page view.
 */
export const PageLayout = ({ children, className, scrolling = 'document' }: PageLayoutProps) => {
  return (
    <div
      className={cn(
        'bg-primary-gradient flex flex-col items-center px-2 py-4 md:py-8',
        scrolling === 'viewport' ? 'h-dvh overflow-hidden' : 'min-h-dvh',
        className,
      )}
    >
      <div className="m-0 flex min-h-0 w-full max-w-lg flex-1 flex-col items-center p-0">
        {children}
      </div>
    </div>
  );
};
